import * as yup from 'yup';
import type { AdSetEntry } from '../api/getAdSets';
import type { AdSettingEntry } from 'features/tiktok/api';

export const tikTokSchema = yup
  .object()
  .shape({
    currentStep: yup.number().default(0).optional(),
    app: yup.string().required("Can't be empty"),
    accountId: yup.string().when('app', {
      is: (val: string) => !val,
      then: (schema) => schema.required('Application should be chosen first'),
      otherwise: (schema) => schema.required("Can't be empty"),
    }),
    adType: yup.string().required("Can't be empty"),
    selectedCampaigns: yup.array().of(yup.string().required()),
    selectedCampaignsOnly: yup.bool(),
    selectedAdGroups: yup.array().of(yup.string().required()),
    selectedCreatives: yup.array(),
    selectedCreativesRows: yup.array(),
    packsWithCreatives: yup
      .array()
      .of(
        yup.object().shape({
          tag: yup.string().default(''),
          creatives: yup.array().of(yup.string().required()).required(),
          filterCreativeByName: yup
            .array()
            .of(yup.string().required())
            .default([]),
        })
      )
      .default([
        {
          tag: '',
          creatives: [],
          filterCreativeByName: [],
          visual_touch_ups: true,
        },
      ]),
    activePacksWithCreatives: yup.number().nullable().optional(),
    activePackTag: yup.string().default(''),
    filterCreativeByName: yup.string(),
    filterCampaignsByName: yup.array(),
    filterAdSetsByName: yup.array(),
    $adSetsData: yup.mixed<AdSetEntry[]>().optional(),
    $adSettingsData: yup.mixed<AdSettingEntry[]>().optional(),
  })
  .test('step-validation', 'Step validation failed', function (value) {
    const currentStep = value.currentStep;

    // Step 1: Campaign selection
    if (
      currentStep === 2 &&
      (!value.selectedCampaigns || value.selectedCampaigns.length === 0)
    ) {
      return this.createError({
        path: 'selectedCampaigns',
        message: 'At least one campaign has to be selected',
      });
    }

    // Step 1: Creatives selection
    if (
      currentStep === 1 &&
      Array.isArray(value.packsWithCreatives) &&
      value.packsWithCreatives.length > 0
    ) {
      const emptyCreativesIndex = value.packsWithCreatives.findIndex(
        (pack) => !pack.creatives || pack.creatives.length === 0
      );

      if (emptyCreativesIndex !== -1) {
        return this.createError({
          path: `packsWithCreatives[${emptyCreativesIndex}].creatives`,
          message: 'At least one creative has to be selected',
        });
      }
    }

    // Step 2: Ad groups selection
    if (currentStep === 3) {
      if (!value.selectedCampaigns || value.selectedCampaigns.length === 0) {
        return this.createError({
          path: 'selectedAdGroups',
          message: 'Campaigns must be selected first',
        });
      }

      if (!value.selectedAdGroups || value.selectedAdGroups.length === 0) {
        return this.createError({
          path: 'selectedAdGroups',
          message:
            'At least one ad group must be selected for each selected campaign',
        });
      }

      const adSetsData = value.$adSetsData;
      if (adSetsData && Array.isArray(adSetsData) && value.selectedAdGroups) {
        const hasValidAdGroups = value.selectedCampaigns.every(
          (campaignId: string) => {
            const campaign = adSetsData.find(
              (c: AdSetEntry) => c.campaign_id === campaignId
            );
            if (!campaign) return true;

            return campaign.ad_groups.some((adGroup) =>
              value.selectedAdGroups?.includes(adGroup.adgroup_id)
            );
          }
        );

        if (!hasValidAdGroups) {
          return this.createError({
            path: 'selectedAdGroups',
            message:
              'At least one ad group must be selected for each selected campaign',
          });
        }
      }
    }

    return true;
  });
